import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, ChevronLeft, ChevronRight, Check, AlertCircle } from 'lucide-react';
import { DiscGrupo, DiscFator, DiscResposta } from '../shared/atlasApi';

interface Props {
  grupos: DiscGrupo[];
  salvando: boolean;
  erro: string | null;
  onConcluir: (respostas: DiscResposta[]) => void;
  /** Sem onCancelar, a primeira pergunta não tem botão de voltar. */
  onCancelar?: () => void;
}

const FATORES: DiscFator[] = ['D', 'I', 'S', 'C'];

/** As 24 perguntas com progresso e navegação. Usado dentro do Atlas e na página pública. */
const PassosQuestionario: React.FC<Props> = ({ grupos, salvando, erro, onConcluir, onCancelar }) => {
  const [idx, setIdx] = useState(0);
  const [respostas, setRespostas] = useState<Record<number, { mais?: DiscFator; menos?: DiscFator }>>({});
  const [erroLocal, setErroLocal] = useState<string | null>(null);

  const total = grupos.length;
  const grupo = grupos[idx];
  const ans = grupo ? respostas[grupo.id] || {} : {};
  const respondida = !!(ans.mais && ans.menos && ans.mais !== ans.menos);
  const totalRespondidas = grupos.filter(g => {
    const a = respostas[g.id];
    return a && a.mais && a.menos && a.mais !== a.menos;
  }).length;

  const escolher = (tipo: 'mais' | 'menos', fator: DiscFator) => {
    if (!grupo) return;
    setRespostas(prev => {
      const atual = { ...(prev[grupo.id] || {}) };
      if (tipo === 'mais') {
        atual.mais = atual.mais === fator ? undefined : fator;
        if (atual.menos === atual.mais) atual.menos = undefined;
      } else {
        atual.menos = atual.menos === fator ? undefined : fator;
        if (atual.mais === atual.menos) atual.mais = undefined;
      }
      return { ...prev, [grupo.id]: atual };
    });
  };

  const irAnterior = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);
  const irProxima = useCallback(() => {
    if (respondida) setIdx(i => Math.min(total - 1, i + 1));
  }, [respondida, total]);

  // Navegação por setas do teclado
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') irAnterior();
      else if (e.key === 'ArrowRight') irProxima();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [irAnterior, irProxima]);

  const concluir = () => {
    const payload: DiscResposta[] = grupos.map(g => {
      const a = respostas[g.id] || {};
      return { grupo_id: g.id, mais: a.mais as DiscFator, menos: a.menos as DiscFator };
    });
    if (payload.some(p => !p.mais || !p.menos || p.mais === p.menos)) {
      setErroLocal('Responda todas as perguntas antes de concluir.');
      return;
    }
    setErroLocal(null);
    onConcluir(payload);
  };

  const ehUltima = idx === total - 1;
  const msgErro = erroLocal || erro;

  return (
    <div className="space-y-4">
      {/* Progresso */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700">Pergunta {idx + 1} de {total}</span>
          <span className="text-xs text-gray-400">{totalRespondidas}/{total} respondidas</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-purple-700 transition-all"
            style={{ width: `${((idx + 1) / total) * 100}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {grupos.map((g, i) => {
            const a = respostas[g.id];
            const ok = a && a.mais && a.menos && a.mais !== a.menos;
            return (
              <button
                key={g.id}
                onClick={() => { if (i <= idx || ok) setIdx(i); }}
                title={`Pergunta ${i + 1}`}
                className={`w-5 h-5 rounded-full text-[9px] font-bold transition ${
                  i === idx ? 'bg-purple-600 text-white ring-2 ring-purple-200'
                  : ok ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-400'
                }`}
              >
                {ok ? '✓' : i + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Pergunta */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <p className="text-sm text-gray-600 mb-4">
          Marque o adjetivo que <strong className="text-emerald-600">MAIS</strong> combina com você
          e o que <strong className="text-rose-600">MENOS</strong> combina.
        </p>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-[10px] uppercase tracking-wider text-gray-400 font-semibold px-1">
            <span>Adjetivo</span>
            <span className="w-16 text-center">Mais</span>
            <span className="w-16 text-center">Menos</span>
          </div>
          {grupo && FATORES.map(f => (
            <div key={f} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
              <span className="text-sm text-gray-800 capitalize px-1">{grupo.adjetivos[f]}</span>
              <button
                onClick={() => escolher('mais', f)}
                className={`w-16 py-2 rounded-lg text-xs font-medium border transition ${
                  ans.mais === f
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-emerald-300'
                }`}
              >
                Mais
              </button>
              <button
                onClick={() => escolher('menos', f)}
                className={`w-16 py-2 rounded-lg text-xs font-medium border transition ${
                  ans.menos === f
                    ? 'bg-rose-500 border-rose-500 text-white'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-rose-300'
                }`}
              >
                Menos
              </button>
            </div>
          ))}
        </div>
      </div>

      {msgErro && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {msgErro}
        </div>
      )}

      {/* Navegação */}
      <div className="flex items-center justify-between">
        {idx === 0 && !onCancelar ? <span /> : (
          <button
            onClick={idx === 0 ? onCancelar : irAnterior}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> {idx === 0 ? 'Cancelar' : 'Anterior'}
          </button>
        )}
        {ehUltima ? (
          <button
            onClick={concluir}
            disabled={!respondida || salvando}
            className="px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center gap-2"
          >
            {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Concluir
          </button>
        ) : (
          <button
            onClick={irProxima}
            disabled={!respondida}
            className="px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium flex items-center gap-1"
          >
            Próxima <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PassosQuestionario;
